import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  skip,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  ObjectCardExpandedState,
  ObjectCardKind,
  ObjectCardSettingsType,
  ObjectCardType,
} from '@common/components/object-card';
import { IObjectCardAdapter } from '@common/components/object-card-adapter';
import { AppService } from '@services/app.service';
import { GeozonesService } from '@services/geozones.service';
import {
  clearObjectCardData,
  closeFixedObjectCard,
  closeObjectCardOnMap,
  loadObjectCardData,
} from '@store/monitoring/monitoring.actions';
import { selectMonitoringById, selectObjectCard } from '@store/monitoring/monitoring.selectors';
import { selectCurrentUserObjectCardSettingsByKind } from '@store/selectors/current_user.selectors';

import { MonitoringItem } from '../../../pages/monitoring-page/monitoring.types';

/**
 * Пауза перед запросом фиксированной карточки после того, как она оказалась на экране
 * (остановка прокрутки, раскрытие) или изменился `rt` объекта. Прокрутку отсекает список
 * (`listScrolling`), поэтому пауза короткая — только чтобы схлопнуть серию событий подряд.
 */
const FIXED_CARD_REFRESH_DELAY_MS = 100;

/** Данные карточки отстают от списка мониторинга (или их ещё нет) */
function isObjectCardStale(card: ObjectCardType | null, monitoringRt: number | null): boolean {
  if (!card) return true;

  return (card.general?.lastMessage?.rt ?? 0) < (monitoringRt ?? 0);
}

@Injectable({
  providedIn: 'root',
})
export class ObjectCardAdapterService implements IObjectCardAdapter<MonitoringItem> {
  private readonly store = inject(Store);
  private readonly geozonesService = inject(GeozonesService);
  private readonly appService = inject(AppService);

  isClickableGeozones = true;

  /**
   * Фиксированная карточка сразу не грузится: открытых может быть много, и они постоянно
   * входят в отрендеренный диапазон списка при прокрутке. Её запрос уходит из
   * {@link startAutoRefresh$} — с задержкой и только если данные в сторе устарели.
   */
  loadObjectCardData(objectId: number, type: ObjectCardKind): void {
    if (type === 'fixed') return;

    this.store.dispatch(loadObjectCardData({ objectId, cardType: type }));
  }

  /**
   * Возвращает Observable, который инициирует обновление данных карточки
   * при внешнем изменении объекта (WebSocket и т.п.).
   * Эмиты из стора, вызванные собственными запросами, игнорируются.
   */
  startAutoRefresh$(objectId: number, type: ObjectCardKind): Observable<void> {
    if (type === 'fixed') {
      return this.fixedCardRefresh$(objectId);
    }

    return new Observable<void>((_) => {
      const sub = this.store
        .select(selectMonitoringById(objectId))
        .pipe(
          distinctUntilChanged((a, b) => a?.lastMessage?.rt === b?.lastMessage?.rt),
          skip(1), // пропускаем эмит от начального loadObjectCardData, вызванного в компоненте
          debounceTime(100),
        )
        .subscribe(() => {
          this.loadObjectCardData(objectId, type);
        });

      return () => sub.unsubscribe();
    });
  }

  /**
   * Обновление фиксированной карточки: на каждое изменение `rt` объекта (включая текущее
   * значение при подписке) ждём паузу и запрашиваем данные, только если они отстают
   * от списка. Подписка живёт, пока карточка на экране — отписка отменяет и ожидающий запрос.
   */
  private fixedCardRefresh$(objectId: number): Observable<void> {
    return this.store.select(selectMonitoringById(objectId)).pipe(
      map((item) => item?.lastMessage?.rt ?? null),
      distinctUntilChanged(),
      debounceTime(FIXED_CARD_REFRESH_DELAY_MS),
      withLatestFrom(this.store.select(selectObjectCard('fixed', objectId))),
      filter(([rt, card]) => isObjectCardStale(card, rt)),
      tap(() => this.store.dispatch(loadObjectCardData({ objectId, cardType: 'fixed' }))),
      map(() => undefined),
    );
  }

  getOpenedObjectCard$(objectId: number, type: ObjectCardKind): Observable<ObjectCardType | null> {
    return this.store.select(selectObjectCard(type, objectId));
  }

  getMonitoringObject$(objectId: number): Observable<MonitoringItem | undefined> {
    return this.store.select(selectMonitoringById(objectId));
  }

  getCurrentUserObjectCardSettings$(
    type: ObjectCardKind,
  ): Observable<ObjectCardSettingsType | null> {
    return this.store.select(selectCurrentUserObjectCardSettingsByKind(type));
  }

  closeObjectCard(objectId: number, type: ObjectCardKind): void {
    if (type === 'fixed') {
      this.store.dispatch(closeFixedObjectCard({ id: objectId }));
      this.appService.resetFixedObjectCardExpandedState(objectId);
      return;
    }

    this.store.dispatch(closeObjectCardOnMap());
  }

  /**
   * Данные всплывающей карточки живут, пока открыт хотя бы один её экземпляр.
   * Фиксированная переживает уход со страницы мониторинга — её данные не трогаем,
   * они удаляются при закрытии карточки или удалении объекта.
   */
  clearObjectCard(objectId: number, type: ObjectCardKind): void {
    if (type === 'fixed') return;

    this.store.dispatch(clearObjectCardData({ objectId, cardType: type }));
  }

  goToGeozoneBounds(geozoneId: number): void {
    this.geozonesService.goToGeozoneBounds.next(geozoneId);
  }

  getExpandedItemsState(objectId: number, type: ObjectCardKind): ObjectCardExpandedState {
    return type === 'fixed'
      ? this.appService.getFixedObjectCardExpandedState(objectId)
      : this.appService.stateObjectCardSettings;
  }
}
